import { mapValues, remove } from "lodash-es";
import { ErrorCode, headers, JSONCodec } from "nats.ws";
import type { NatsConnection } from "nats.ws";

import { AlmsyncAdoApiClient } from "@/backend/natsstubs/almsync/ado/client";
import { AlmsyncCommonApiClient } from "@/backend/natsstubs/almsync/common/client";
import type { GetALMIssuesRequest } from "@/backend/natsstubs/almsync/common/models";
import { AlmsyncJiraApiClient } from "@/backend/natsstubs/almsync/jira/client";
import type { GetDevLinksResponse } from "@/backend/natsstubs/almsync/models";
import { AlmsyncRallyApiClient } from "@/backend/natsstubs/almsync/rally/client";
import { AlmsyncUserMappingsApiClient } from "@/backend/natsstubs/almsync/user_mappings/client";
import { BoardApiClient } from "@/backend/natsstubs/board/client";
import type {
  GetDiffRequest,
  GetEventLogRequest,
  NoData,
  StickyUpdatedEvent,
  StickyUpdateEndedEvent,
} from "@/backend/natsstubs/board/models";
import { BoardApiPublisher } from "@/backend/natsstubs/board/publisher";
import { ConfidenceVoteApiClient } from "@/backend/natsstubs/confidence_vote/client";
import type {
  AddVoteRequest,
  ConfidenceVote,
  ConfidenceVoteSession,
  CreateConfidenceVoteSessionRequest,
  Participant,
  UpdateConfidenceVoteSessionRequest,
} from "@/backend/natsstubs/confidence_vote/models";
import { FlexboardApiClient } from "@/backend/natsstubs/flexboard/client";
import { OrgCompaniesApiClient } from "@/backend/natsstubs/org_companies/client";
import { PiserverApiClient } from "@/backend/natsstubs/piserver/client";
import { SessionApiClient } from "@/backend/natsstubs/session/client";
import type { ChartScope } from "@/backend/natsstubs/session/models";
import { SightlineApiClient } from "@/backend/natsstubs/sightline/client";
import type { SendMessageRequest, SendMessageResponse } from "@/backend/natsstubs/sightline/models";
import { SightlineApiPublisher } from "@/backend/natsstubs/sightline/publisher";
import { StatusApiClient } from "@/backend/natsstubs/status/client";
import { StickyApiClient } from "@/backend/natsstubs/sticky/client";
import type { RemoveStickyRequest } from "@/backend/natsstubs/sticky/models";
import { StickyTypeApiClient } from "@/backend/natsstubs/sticky_type/client";
import type { ApiCaller } from "@/backend/natsstubs/wire/ApiCaller";
import type {
  ApiSubscribe,
  ApiSubscriber,
  ApiSubscription,
} from "@/backend/natsstubs/wire/ApiSubscriber";
import type { ConnectionContext } from "@/backend/natsstubs/wire/context";
import { ForbiddenPublisherError } from "@/backend/natsstubs/wire/error";
import { NatsApiCaller } from "@/backend/natsstubs/wire/nats/NatsApiCaller";
import { NatsApiSubscriber } from "@/backend/natsstubs/wire/nats/NatsApiSubscriber";
import { keysToCamel } from "@/backend/natsstubs/wire/transform";
import { toApiInitObjective, toApiObjective } from "@/backend/write/toApiData";
import { captureException, captureMessage } from "@/error/sentry";
import { backlogSyncTimeout, iterationSyncTimeout } from "@/model/Settings";
import type { ArtId, BoardType } from "@/model/baseTypes";
import type { Reaction } from "@/model/card";
import type { Objective } from "@/model/objective";
import type { SessionAlmStatus } from "@/model/session";
import type { Shape } from "@/model/shape";
import type { TimerData } from "@/model/timer";
import type { TechnicalUser } from "@/model/user";
import { goToLogout } from "@/router/navigation";
import type { EventFilter } from "@/store/activity";
import { useClientStore } from "@/store/client";
import { useConnectionStore } from "@/store/connection";
import { useStatisticsStore } from "@/store/statistics";
import { isNoUser } from "@/store/user";
import { isFeatureEnabled, parseUrlWithoutRouter } from "@/utils/env/feature";
import { stickerToString } from "@/utils/sticker";
import type { UnknownRecord } from "@/utils/type";

import { BackendError } from "../BackendError";
import type { Connection } from "../Connection";
import { handlePermissionsViolation } from "../permissionsViolation";
import type {
  SearchStickiesQuery,
  BackendReadSession,
  SubscriptionHandler,
  SubscriptionLevel,
} from "../read/BackendReadSession";
import { EventInfo } from "../read/EventInfo";
import type {
  NormalizedServerAlmItemTypeMap,
  ServerAlmInfo,
  ServerAlmItemType,
  ServerAlmItemTypeMap,
  ServerAlmSource,
  ServerAlmStickyTypeData,
  ServerArt,
  ServerBacklogSyncState,
  ServerBoard,
  ServerBoardEventsResponse,
  ServerBoardIteration,
  ServerCategory,
  ServerCompanySettings,
  ServerEvent,
  ServerFlexBoard,
  ServerFlexType,
  ServerInfo,
  ServerIterationSyncState,
  ServerKanbanConfig,
  ServerKanbanConfigMap,
  ServerLicense,
  ServerLink,
  ServerMirrorResponse,
  ServerObjectives,
  ServerSearchResult,
  ServerSettings,
  ServerStatusWithData,
  ServerStickerType,
  ServerStickiesUpdate,
  ServerSticky,
  ServerStickyChange,
  ServerSession,
  ServerStickyGroupBoardIds,
  ServerStickyType,
} from "../serverModel";
import { isNormalizedServerAlmItemTypeMap } from "../serverModel";
import type { BackendWriteSession } from "../write/BackendWriteSession";
import { BaseBackendSession } from "./BaseBackendSession";
import { denormalizedServerAlmItemTypeMap } from "./BaseBackendSession.utils";
import { NatsApiCallerWrapper } from "./NatsApiCallerWrapper";
import { NatsApiSubscriberWrapper } from "./NatsApiSubscriberWrapper";
import type { RpcBackend } from "./RpcBackend";
import {
  mapApiBacklogSyncState,
  mapApiIterationSyncState,
  mapApiKanbanConfigs,
  unwrapAlmItemType,
  unwrapAlmTypes,
  unwrapProjectSources,
} from "./mapApiAlm";
import {
  mapApiStateSticky,
  mapApiStickiesResponse,
  mapApiSticky,
  mapApiStickyChange,
  toApiPartialSticky,
} from "./mapApiBoard";
import { mapApiBoardObjectives } from "./mapApiBoardObjectives";
import { mapApiCompanySettings, mapApiLicense } from "./mapApiCompany";
import { mapApiCategory, mapApiFlexType } from "./mapApiFlexboard";
import { mapApiAlmSessionInfo, mapApiServerInfo, mapApiStatusData } from "./mapApiInfo";
import {
  mapApiAlmStatus,
  mapApiArt,
  mapApiBoardInfo,
  mapApiBoardIterations,
  mapApiLink,
  mapApiSession,
  mapApiSearchResult,
  mapApiStickyType,
  mapApiGroupBoards,
  mapApiTimerEvent,
} from "./mapApiSession";

// The server has a shared 120s generation deadline; allow context and transport headroom.
const SIGHTLINE_ANSWER_TIMEOUT_MS = 150_000;

// Module-scoped: lives only for this page load. An SPA-only relogin (the logout
// page's `login()` branch navigates via the router rather than reloading)
// intentionally keeps this state — it is not reset on relogin, and that's fine.
const capturedForbiddenPublishers = new Set<string>();

export class NatsBackendSession
  extends BaseBackendSession
  implements BackendReadSession, BackendWriteSession
{
  private readonly codec = JSONCodec();
  private readonly userId;
  private readonly apiCaller: ApiCaller;
  private readonly apiSubscriber: ApiSubscriber;
  private loggingOut = false;

  private confidenceVoteClient: ConfidenceVoteApiClient;
  private stickyClient: StickyApiClient;
  private stickyTypeClient: StickyTypeApiClient;
  private almsyncJiraClient: AlmsyncJiraApiClient;
  private almsyncAdoClient: AlmsyncAdoApiClient;
  private almsyncRallyClient: AlmsyncRallyApiClient;
  private almClient: AlmsyncCommonApiClient;
  private almUserMappingsClient: AlmsyncUserMappingsApiClient;
  private sessionClient: SessionApiClient;
  private boardClient: BoardApiClient;
  private flexboardClient: FlexboardApiClient;
  private statusClient: StatusApiClient;
  private piserverClient: PiserverApiClient;
  private sightlineClient: SightlineApiClient;
  private orgCompaniesClient: OrgCompaniesApiClient;
  private sightlinePublisher: SightlineApiPublisher;
  private boardPublisher: BoardApiPublisher;

  // Gate for PI-187: route board/sticky RPC through the generated NATS stubs
  // (pi-web direct) instead of the legacy rpcBackend (pi-bridge -> WAMP).
  // Resolved per call: initFeatureToggles() races openConnection() in login.ts,
  // so a constructor snapshot could pin the flag to false for the whole session.
  // When off, every migrated method delegates to super.
  private get natsPiweb(): boolean {
    return isFeatureEnabled({ query: parseUrlWithoutRouter() }, "nats-piweb");
  }

  constructor(
    private connection: Connection,
    user: Pick<TechnicalUser, "company" | "id" | "role" | "natsScheme">,
    rpcBackend: RpcBackend,
  ) {
    super(user.company, rpcBackend);
    this.userId = user.id;
    connection.onError = (msg, info) => {
      const report = () => {
        const conn = useConnectionStore();
        captureMessage(`NATS error: ${msg}`, {
          info,
          subscriptions: {
            ...mapValues(this.subscriptions, (levelSubs) =>
              levelSubs.map((sub) => sub.getSubject()),
            ),
            RPC: rpcBackend.calls,
          },
          connection: {
            lastFreeze: conn.lastFrozen > 0 ? new Date(conn.lastFrozen).toISOString() : undefined,
            lastUnfreeze:
              conn.lastUnfrozen > 0 ? new Date(conn.lastUnfrozen).toISOString() : undefined,
            msSinceLastUnfreeze: conn.timeSinceLastUnfreeze,
            states: conn.wsStates(),
          },
        });
      };
      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
      if (msg === ErrorCode.PermissionsViolation) {
        void handlePermissionsViolation(report);
      } else {
        report();
      }
    };

    const connectionContext: ConnectionContext<NatsConnection> = {
      connection,
      userId: user.id,
      clientId: useClientStore().clientId,
      defaultTimeout: 30000,
      scheme: user.natsScheme,
    };
    this.apiCaller = new NatsApiCallerWrapper(
      new NatsApiCaller(
        connectionContext,
        { companyId: user.company, userId: user.id, role: user.role },
        (context) => ({
          ...context,
          correlationId: useClientStore().newCorrelationId(),
        }),
      ),
    );
    this.apiSubscriber = new NatsApiSubscriberWrapper(
      new NatsApiSubscriber(
        connectionContext,
        { companyId: user.company },
        {
          onSubscribeError: (error, subject) => {
            if (!this.loggingOut) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
              this.loggingOut = error.code === ErrorCode.PermissionsViolation;
              const logOut = () => {
                try {
                  captureException(error, { subscriptions: { origin: "subscription", subject } });
                } finally {
                  if (this.loggingOut) {
                    void goToLogout("cannot-subscribe");
                  }
                }
              };
              // loggingOut stays set while the refresh runs, so the burst of
              // errors from the other subscriptions does not log out behind it.
              if (this.loggingOut) {
                void handlePermissionsViolation(logOut);
              } else {
                logOut();
              }
            }
          },
          onHandleError: (error, subject) => {
            if (error instanceof ForbiddenPublisherError) {
              // One misbehaving publisher would otherwise raise one Sentry event
              // per event per subscribed browser.
              if (capturedForbiddenPublishers.has(subject)) {
                return;
              }
              capturedForbiddenPublishers.add(subject);
            }
            captureException(error, { info: { subject } });
          },
        },
      ),
      this.subscriptionsPaused,
    );
    this.confidenceVoteClient = new ConfidenceVoteApiClient(this.apiCaller);
    this.stickyClient = new StickyApiClient(this.apiCaller);
    this.stickyTypeClient = new StickyTypeApiClient(this.apiCaller);
    this.almsyncJiraClient = new AlmsyncJiraApiClient(this.apiCaller);
    this.almsyncAdoClient = new AlmsyncAdoApiClient(this.apiCaller);
    this.almsyncRallyClient = new AlmsyncRallyApiClient(this.apiCaller);
    this.almClient = new AlmsyncCommonApiClient(this.apiCaller);
    this.almUserMappingsClient = new AlmsyncUserMappingsApiClient(this.apiCaller);
    this.sessionClient = new SessionApiClient(this.apiCaller);
    this.boardClient = new BoardApiClient(this.apiCaller);
    this.flexboardClient = new FlexboardApiClient(this.apiCaller);
    this.statusClient = new StatusApiClient(this.apiCaller);
    this.piserverClient = new PiserverApiClient(this.apiCaller);
    this.sightlineClient = new SightlineApiClient(this.apiCaller);
    this.orgCompaniesClient = new OrgCompaniesApiClient(this.apiCaller);
    this.sightlinePublisher = new SightlineApiPublisher(this.apiCaller);
    this.boardPublisher = new BoardApiPublisher(this.apiCaller);
  }

  apiSubscribe<T>(
    subscribe: ApiSubscribe<T>,
    handler: T,
    params: Record<string, string> = {},
    level: SubscriptionLevel = "session",
  ): ApiSubscription[] {
    const subscriptions = subscribe(
      this.apiSubscriber,
      // Callers may override sessionId: almsync events key on the sync
      // session id, not the planning session.
      { sessionId: this.sessionId, ...params },
      handler,
    );
    for (const subscription of subscriptions) {
      this.subscriptions[level].push(subscription);
    }
    return subscriptions;
  }

  doSubscribe<TArgs>(path: string, handler: SubscriptionHandler<TArgs>, level: SubscriptionLevel) {
    try {
      const subscription = this.connection.subscribe(path, {
        callback: (error, msg) => {
          if (error) {
            if (!this.loggingOut) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
              this.loggingOut = error.code === ErrorCode.PermissionsViolation;
              try {
                captureException(error, { subscriptions: { origin: "subscription", topic: path } });
              } finally {
                if (this.loggingOut) {
                  void goToLogout("cannot-subscribe");
                }
              }
            }
            return;
          }
          if (
            this.subscriptionsPaused.value ||
            msg.headers?.get?.("clientId") === useClientStore().clientId
          ) {
            // Ignore the messages we sent ourselves
            return;
          }
          useStatisticsStore().addOk("receives");
          // TODO casting to TArgs is not safe
          const body = this.codec.decode(msg.data) as { args: TArgs; string: unknown };
          const { args, ...kwargs } = body;
          const details = new EventInfo(msg.subject);
          void handler(args, kwargs, details);
        },
      });
      this.subscriptions[level].push(subscription);
      return {
        unsubscribe: () => {
          void this.unsubscribe(subscription).then(() =>
            remove(this.subscriptions[level], (sub) => sub.getID() === subscription.getID()),
          );
        },
      };
    } catch (error) {
      throw new BackendError("Subscribe Error", `Error subscribing ${path}`, {
        request: { path },
        response: { error },
      });
    }
  }

  override doUnsubscribeLevel(level: SubscriptionLevel, connectionOpen: boolean) {
    return super.doUnsubscribeLevel(level, !this.connection.isClosed() && connectionOpen);
  }

  almSubscribe<TArgs>(path: string, handler: SubscriptionHandler<TArgs>) {
    if (this.almSyncId) {
      this.doSubscribe(this.almTopic(path, { subscribe: true }), handler, "session");
    }
  }

  roomSubscribe<TArgs>(path: string, handler: SubscriptionHandler<TArgs>) {
    this.doSubscribe(this.roomTopic(path, { subscribe: true }), handler, "room");
  }

  pubsubSubscribe<TArgs>(path: string, handler: SubscriptionHandler<TArgs>) {
    const transformingHandler: SubscriptionHandler<TArgs> = (args, kwargs, event) => {
      const transformedArgs = keysToCamel(args) as TArgs;
      const transformedKwargs = keysToCamel(kwargs);
      return handler(transformedArgs, transformedKwargs, event);
    };
    return this.doSubscribe(this.pubsubTopic(path), transformingHandler, "session");
  }

  sessionSubscribe<TArgs>(path: string, handler: SubscriptionHandler<TArgs>) {
    this.doSubscribe(this.sessionTopic(path, { subscribe: true }), handler, "session");
  }

  boardSubscribe<TArgs>(
    boardId: string,
    path: string,
    level: SubscriptionLevel,
    handler: SubscriptionHandler<TArgs>,
  ) {
    const topic = this.sessionBoardTopic(boardId, path, { subscribe: true });
    this.doSubscribe(topic, handler, level);
  }

  sessionPublish(path: string, value: unknown[]) {
    this.doPublish(this.sessionTopic(path, { subscribe: false }), value);
  }

  boardPublish(boardId: string, path: string, value: unknown[]) {
    if (!boardId) {
      captureMessage("Publish with empty boardId", { info: { path, boardId } });
      return;
    }
    this.doPublish(this.sessionBoardTopic(boardId, path, { subscribe: false }), value);
  }

  boardCall<T>(path: string, args?: unknown[], kwargs?: UnknownRecord): Promise<T> {
    return this.rpcBackend.boardCall(path, args ?? [], kwargs);
  }

  stickyCall<T>(path: string, args?: unknown[], kwargs?: UnknownRecord): Promise<T> {
    return this.rpcBackend.stickyCall(path, args ?? [], kwargs);
  }

  private doPublish(topic: string, value: unknown[]) {
    if (this.connection.isClosed()) {
      return;
    }
    try {
      const callHeaders = headers();
      callHeaders.append("correlation-id", useClientStore().newCorrelationId());
      callHeaders.append("clientId", useClientStore().clientId);

      this.connection.publish(topic, this.codec.encode({ args: value }), { headers: callHeaders });
      useStatisticsStore().addOk("publishes");
    } catch (error) {
      useStatisticsStore().addFail("publishes", error);
      throw error;
    }
  }

  private basePath(module: string, subscribe = true) {
    return `piplanning.${module}.6.${this.company}.${subscribe ? "*" : this.userId}`;
  }

  private sessionBoardTopic(boardId: string, path: string, { subscribe = true } = {}) {
    const base = this.basePath("board", subscribe);
    boardId = boardId === "" ? "*" : boardId;
    return `${base}.${this.sessionId}.${boardId}.${path}`;
  }

  private roomTopic(path: string, { subscribe = true } = {}) {
    return `${this.basePath("session", subscribe)}.${path}`;
  }

  private sessionTopic(path: string, { subscribe = true } = {}) {
    return `${this.basePath("session", subscribe)}.${this.sessionId}.${path}`;
  }

  private almTopic(path: string, { subscribe = true } = {}) {
    return `${this.basePath("almsession", subscribe)}.${this.almSyncId}.${path}`;
  }

  private pubsubTopic(path: string) {
    return `pip.${this.company}.pubsub.${this.sessionId}.${path}`;
  }

  async getStickerTypes(): Promise<ServerStickerType[]> {
    const response = await this.sessionClient.getStickerTypes(this.sessionId);
    return response.stickerTypes.map((st) => ({
      icon: st.icon,
      color: st.color,
      label: st.label,
    }));
  }

  async addObjectiveObjectiveLink(
    fromBoardId: string,
    fromObjectiveId: string,
    toBoardId: string,
    toObjectiveId: string,
  ): Promise<void> {
    await this.sessionClient.createObjectiveObjectiveLink({
      fromBoardId,
      fromObjectiveId,
      toBoardId,
      toObjectiveId,
    });
  }

  async deleteObjectiveObjectiveLink(
    fromBoardId: string,
    fromObjectiveId: string,
    toBoardId: string,
    toObjectiveId: string,
  ): Promise<void> {
    await this.sessionClient.deleteObjectiveObjectiveLinks({
      fromBoardId,
      fromObjectiveId,
      toBoardId,
      toObjectiveId,
    });
  }

  async createConfidenceVoteSession(
    payload: CreateConfidenceVoteSessionRequest,
  ): Promise<ConfidenceVoteSession> {
    const res = await this.confidenceVoteClient.createSession(payload);
    return res.session;
  }

  async getConfidenceVoteSessions(piSessionId?: string): Promise<ConfidenceVoteSession[]> {
    const res = await this.confidenceVoteClient.searchSessions({ piSessionId });
    return res.sessions;
  }

  async endConfidenceVoteSession(votingSessionId: string): Promise<ConfidenceVoteSession> {
    const res = await this.confidenceVoteClient.endSession(votingSessionId);
    return res.session;
  }

  async joinConfidenceVoteSession(votingSessionId: string): Promise<ConfidenceVoteSession> {
    const res = await this.confidenceVoteClient.joinSession(votingSessionId);
    return res.session;
  }

  async getConfidenceVoteSessionParticipants(votingSessionId: string): Promise<Participant[]> {
    const res = await this.confidenceVoteClient.getParticipants(votingSessionId);
    return res.participants;
  }

  async getMyVote(votingSessionId: string): Promise<ConfidenceVote | null> {
    const res = await this.confidenceVoteClient.getMyVote(votingSessionId);
    return res.vote ?? null;
  }

  async addVote(votingSessionId: string, payload: AddVoteRequest): Promise<void> {
    await this.confidenceVoteClient.addVote(payload, votingSessionId);
  }

  async deleteConfidenceVoteSession(votingSessionId: string): Promise<void> {
    await this.confidenceVoteClient.deleteSession(votingSessionId);
  }

  async updateConfidenceVoteSession(
    votingSessionId: string,
    payload: UpdateConfidenceVoteSessionRequest,
  ): Promise<ConfidenceVoteSession> {
    const res = await this.confidenceVoteClient.updateSession(payload, votingSessionId);
    return res.session;
  }

  async startConfidenceVoteSession(votingSessionId: string): Promise<ConfidenceVoteSession> {
    const res = await this.confidenceVoteClient.startSession(votingSessionId);
    return res.session;
  }

  async deleteCard(cardId: string, payload: RemoveStickyRequest): Promise<void> {
    await this.stickyClient.deleteSticky(payload, cardId);
  }

  async sendSightlineMessage(request: SendMessageRequest): Promise<SendMessageResponse> {
    return await this.sightlineClient.createMessage(
      request,
      this.sessionId,
      SIGHTLINE_ANSWER_TIMEOUT_MS,
    );
  }

  async stopSightlineMessage(messageId: string): Promise<void> {
    await this.sightlinePublisher.messageStopped({}, this.sessionId, messageId);
  }

  async getDevLinks(issueKey: string): Promise<GetDevLinksResponse["devLinks"]> {
    if (!this.almSyncId) return [];
    const response = await this.almsyncJiraClient.getDevLinks({ issueKey }, this.almSyncId);
    return response.devLinks;
  }

  async getStatusDistribution(params: {
    boardId?: string;
    teamId?: string;
    iterationId?: number;
    artId?: number;
    stickyId?: string;
    objectiveId?: string;
  }) {
    const response = await this.sessionClient.getStatusDistribution(
      {
        boardId: params.boardId,
        teamId: params.teamId,
        iterationId: params.iterationId,
        artId: params.artId,
        stickyId: params.stickyId,
        objectiveId: params.objectiveId,
      },
      this.sessionId,
    );

    return response.items;
  }

  async getSignals(boardId: string) {
    return this.boardClient.getSignals(boardId);
  }

  async getCapacityAllocation(teamIds: number[]) {
    const response = await this.sessionClient.getCapacityAllocation({ teamIds }, this.sessionId);

    return { data: response.items, warnings: response.warnings ?? [] };
  }

  async getDependencyFlow(teamIds: number[], iterationId: number | null) {
    const response = await this.sessionClient.getDependencyFlow(
      { teamIds, iterationId },
      this.sessionId,
    );

    return { data: response.items, warnings: response.warnings ?? [] };
  }

  async getBurndownChartData(teamIds: number[], scope: ChartScope) {
    const response = await this.sessionClient.getBurndown({ teamIds, scope }, this.sessionId);
    return response.dataPoints;
  }

  // NATS stub requires a non-empty tuple; callers already guarantee at least one team ID
  async getCfdChartData(teamIds: [number, ...number[]], scope: ChartScope) {
    const response = await this.sessionClient.getCfd({ teamIds, scope }, this.sessionId);
    return response.days;
  }

  // NATS stub requires a non-empty tuple; callers already guarantee at least one team ID
  async getPredictability(teamIds: [number, ...number[]], includeArchived: boolean) {
    return await this.sessionClient.getPredictability({ teamIds, includeArchived }, this.sessionId);
  }

  async getAlmIssues(params: GetALMIssuesRequest) {
    return await this.almClient.getAlmIssues(params);
  }

  // The resolved ART Kanban Board columns for this session. pialmsync decides the
  // source by session type (single-ART -> session board; Solution Train -> connection
  // board) and returns null when no board applies.
  async getKanbanConfig(artId: number, force: boolean): Promise<ServerKanbanConfig | null> {
    if (this.getAlmType() !== "jira") return null;
    const { config } = await this.almsyncJiraClient.getJiraSessionKanbanConfig(
      { force },
      this.sessionId,
      artId,
    );
    return config ?? null;
  }

  // --- PI-187 PR1: board/sticky calls migrated to generated NATS stubs ---
  // Each override delegates to super (legacy rpcBackend) when the flag is off,
  // preserving existing behaviour by default.

  override async getScale(boardId: string): Promise<number> {
    if (!this.natsPiweb) return super.getScale(boardId);
    const response = await this.boardClient.getStickyScale(boardId);
    return response.scale;
  }

  override toRisk(
    cardId: string,
    boardId: string,
    text: string,
    typeId: string,
    teamId: string,
  ): void {
    if (!this.natsPiweb) {
      super.toRisk(cardId, boardId, text, typeId, teamId);
      return;
    }
    void this.boardClient.createRiskBoardSticky(
      { id: cardId, text, typeId, teamId: +teamId },
      boardId,
    );
  }

  override async addShape(boardId: string, shape: Shape): Promise<string> {
    if (!this.natsPiweb) return super.addShape(boardId, shape);
    const response = await this.boardClient.addShape({ shape }, boardId);
    return response.id;
  }

  override async editShape(boardId: string, shape: Shape): Promise<void> {
    if (!this.natsPiweb) return super.editShape(boardId, shape);
    await this.boardClient.editShape({ shape }, boardId);
  }

  override async removeShape(boardId: string, shapeId: string): Promise<void> {
    if (!this.natsPiweb) return super.removeShape(boardId, shapeId);
    await this.boardClient.removeShape({ shapeId }, boardId);
  }

  override async getBoardState(id: string, time: Date): Promise<ServerSticky[]> {
    if (!this.natsPiweb) return super.getBoardState(id, time);
    const response = await this.boardClient.getState({ time: time.getTime() }, id);
    return response.results.map(mapApiStateSticky);
  }

  // --- PI-187 PR2: session-domain calls migrated to generated NATS stubs ---

  override async getBoards(sessionId?: string): Promise<Array<ServerBoard | ServerFlexBoard>> {
    if (!this.natsPiweb) return super.getBoards(sessionId);
    const response = await this.sessionClient.getBoards(sessionId || this.sessionId);
    return response.boards.map(mapApiBoardInfo);
  }

  override async getSettings(): Promise<ServerSettings> {
    if (!this.natsPiweb) return super.getSettings();
    const { settings } = await this.sessionClient.getSettings(this.sessionId);
    return { ...settings, disable_delete: this.disableDelete };
  }

  override async getArts(sessionId?: string): Promise<ServerArt[]> {
    if (!this.natsPiweb) return super.getArts(sessionId);
    const response = await this.sessionClient.getArts(sessionId || this.sessionId);
    return response.arts.map(mapApiArt);
  }

  override async getLinks(): Promise<ServerLink[]> {
    if (!this.natsPiweb) return super.getLinks();
    const response = await this.sessionClient.getLinksInSession(this.sessionId);
    return response.links.map(mapApiLink);
  }

  override async addLink(cardId: string, toId: string): Promise<ServerLink> {
    if (!this.natsPiweb) return super.addLink(cardId, toId);
    const response = await this.sessionClient.createLink(
      { link: { fromStickyId: cardId, toStickyId: toId }, fromAlm: false },
      this.sessionId,
    );
    return mapApiLink(response.links[0]);
  }

  override async deleteLink(id: string): Promise<void> {
    if (!this.natsPiweb) return super.deleteLink(id);
    await this.sessionClient.deleteLink({ fromAlm: false }, id);
  }

  override async addObjectiveLink(
    cardId: string,
    boardId: string,
    objectiveId: string,
  ): Promise<void> {
    if (!this.natsPiweb) return super.addObjectiveLink(cardId, boardId, objectiveId);
    await this.sessionClient.createObjectiveLink({ stickyId: cardId, boardId, objectiveId });
  }

  override async getEvents(): Promise<ServerEvent[]> {
    if (!this.natsPiweb) return super.getEvents();
    const response = await this.sessionClient.getTimers(this.sessionId);
    return response.timerEvents.map((event) => mapApiTimerEvent(event, this.sessionId));
  }

  override async createTimerEvent(
    data: Partial<TimerData>,
    target: { boardId?: string; artId?: string },
  ): Promise<string> {
    if (!this.natsPiweb) return super.createTimerEvent(data, target);
    const response = await this.sessionClient.createTimer(
      {
        name: data.name ?? "",
        state: data.state ?? "stopped",
        durationSeconds: data.duration ?? 0,
        end: data.end ?? 0,
        boardId: target.boardId,
        artId: target.artId,
      },
      this.sessionId,
    );
    return response.eventId ?? "";
  }

  override async updateTimerEvent(id: string, data: Partial<TimerData>): Promise<void> {
    if (!this.natsPiweb) return super.updateTimerEvent(id, data);
    await this.sessionClient.updateTimer(
      {
        name: data.name,
        state: data.state,
        durationSeconds: data.duration,
        end: data.end,
      },
      this.sessionId,
      id,
    );
  }

  override async deleteEvent(id: string): Promise<void> {
    if (!this.natsPiweb) return super.deleteEvent(id);
    await this.sessionClient.deleteTimer(this.sessionId, id);
  }

  override async getAlmStatus(): Promise<SessionAlmStatus> {
    if (!this.natsPiweb || !this.almSyncId) return super.getAlmStatus();
    const response = await this.almClient.getStatus(this.almSyncId);
    return mapApiAlmStatus(response.status);
  }

  scaleBoard(boardId: string, value: number) {
    void this.boardPublisher.scaleUpdated(value, this.sessionId, boardId);
  }

  stickyToFront(boardId: string, ids: string[]) {
    void this.boardPublisher.topStickyUpdated(ids, this.sessionId, boardId);
  }

  addObjective(boardId: string, objective: Objective, isStretch: boolean) {
    void this.boardPublisher.objectiveCreated(
      toApiInitObjective(objective, isStretch),
      this.sessionId,
      boardId,
      objective.id,
    );
  }

  deleteObjective(boardId: string, id: string) {
    void this.boardPublisher.objectiveDeleted({}, this.sessionId, boardId, id);
  }

  updateObjective(boardId: string, objective: Objective) {
    void this.boardPublisher.objectiveUpdated(
      { objective: toApiObjective(objective) },
      this.sessionId,
      boardId,
      objective.id,
    );
  }

  moveObjective(boardId: string, objectiveId: string, isStretch: boolean, position: number) {
    void this.boardPublisher.objectiveUpdated(
      { place: { isStretch, position } },
      this.sessionId,
      boardId,
      objectiveId,
    );
  }

  // --- PI-259: sticky change history migrated to generated NATS stubs ---

  override async getBoardDiff(
    id: string,
    from: Date,
    to: Date,
    filters?: EventFilter[],
    changeTypes?: string[],
  ): Promise<ServerStickyChange[]> {
    if (!this.natsPiweb) return super.getBoardDiff(id, from, to, filters, changeTypes);
    const response = await this.boardClient.getDiff(
      changeHistoryRequest(from, to, filters, changeTypes),
      id,
    );
    return response.results.map(mapApiStickyChange);
  }

  override async getBoardEventLog(
    id: string,
    from: Date,
    to: Date,
    filters?: EventFilter[],
    changeTypes?: string[],
  ): Promise<ServerBoardEventsResponse> {
    if (!this.natsPiweb) return super.getBoardEventLog(id, from, to, filters, changeTypes);
    const response = await this.boardClient.getEventLog(
      changeHistoryRequest(from, to, filters, changeTypes),
      id,
    );
    return {
      results: response.results.map(mapApiStickyChange),
      limit: response.limit,
      total: response.total,
      offset: null,
    };
  }

  override async getStickyChanges(id: string): Promise<ServerStickyChange[]> {
    if (!this.natsPiweb) return super.getStickyChanges(id);
    const response = await this.sessionClient.getStickyChanges(id);
    return response.changes.map(mapApiStickyChange);
  }

  // --- PI-394: flexboard RPC via generated NATS stubs ---

  override async getFlexTypes(sessionId?: string): Promise<ServerFlexType[]> {
    if (!this.natsPiweb) return super.getFlexTypes(sessionId);
    const response = await this.flexboardClient.getTypes({
      sessionId: sessionId || this.sessionId,
    });
    return response.types.map(mapApiFlexType);
  }

  override async getCategories(): Promise<ServerCategory[]> {
    if (!this.natsPiweb) return super.getCategories();
    const response = await this.flexboardClient.getCategories({ sessionId: this.sessionId });
    return response.categories.map(mapApiCategory);
  }

  override async addCategory(name: string): Promise<string> {
    if (!this.natsPiweb) return super.addCategory(name);
    const response = await this.flexboardClient.createCategory({
      sessionId: this.sessionId,
      name,
    });
    return response.id;
  }

  override async updateCategory(
    id: string,
    update: { name?: string; position?: number },
  ): Promise<void> {
    if (!this.natsPiweb) return super.updateCategory(id, update);
    await this.flexboardClient.updateCategory(update, id);
  }

  override async deleteCategory(id: string): Promise<void> {
    if (!this.natsPiweb) return super.deleteCategory(id);
    await this.flexboardClient.deleteCategory({ categoryId: id }, id);
  }

  override async addBoard(type: string, name: string): Promise<string> {
    if (!this.natsPiweb) return super.addBoard(type, name);
    const response = await this.flexboardClient.createBoard({
      sessionId: this.sessionId,
      flexboardTypeId: type,
      name,
    });
    return response.id;
  }

  override async updateBoard(id: string, name: string): Promise<void> {
    if (!this.natsPiweb) return super.updateBoard(id, name);
    await this.flexboardClient.updateBoard({ boardId: id, name }, id);
  }

  override async deleteBoard(id: string): Promise<void> {
    if (!this.natsPiweb) return super.deleteBoard(id);
    await this.flexboardClient.deleteBoard({ boardId: id }, id);
  }

  override async duplicateFlex(boardId: string, sessionId: string): Promise<void> {
    if (!this.natsPiweb) return super.duplicateFlex(boardId, sessionId);
    await this.flexboardClient.duplicateBoard({ boardId, targetSessionId: sessionId }, boardId);
  }

  override async addBoardToCategory(boardId: string, categoryId: string): Promise<void> {
    if (!this.natsPiweb) return super.addBoardToCategory(boardId, categoryId);
    await this.flexboardClient.addBoardToCategory({ boardId, categoryId }, boardId);
  }

  override async removeBoardFromCategory(boardId: string, categoryId: string): Promise<void> {
    if (!this.natsPiweb) return super.removeBoardFromCategory(boardId, categoryId);
    await this.flexboardClient.deleteBoardFromCategory({ boardId, categoryId }, boardId);
  }

  override async getLicense(): Promise<ServerLicense> {
    if (!this.natsPiweb) return super.getLicense();
    const response = await this.orgCompaniesClient.getCompanyLicense();
    return mapApiLicense(response.license);
  }

  override async getCompanySettings(): Promise<ServerCompanySettings> {
    if (!this.natsPiweb) return super.getCompanySettings();
    const response = await this.orgCompaniesClient.getCompanySettings();
    return mapApiCompanySettings(response.settings);
  }

  // --- PI-396: status, server info and ALM session info via NATS stubs ---

  override async getAlmSessionInfo(): Promise<ServerAlmInfo> {
    if (!this.natsPiweb) return super.getAlmSessionInfo();
    const response = await this.almClient.getSessionInfo(this.sessionId);
    return mapApiAlmSessionInfo(response);
  }

  override async getServerInfo(useHttp: boolean, timeout?: number): Promise<ServerInfo> {
    // useHttp deliberately keeps the legacy HTTP diagnostics path.
    if (!this.natsPiweb || useHttp) return super.getServerInfo(useHttp, timeout);
    const response = await this.piserverClient.getServerInfo(timeout);
    return mapApiServerInfo(response);
  }

  override async getStatusWithData(
    sizeMB: number,
    useHttp?: boolean,
  ): Promise<ServerStatusWithData> {
    // useHttp deliberately keeps the legacy HTTP diagnostics path.
    if (!this.natsPiweb || useHttp) return super.getStatusWithData(sizeMB, useHttp);
    if (!Number.isFinite(sizeMB) || sizeMB < 0 || sizeMB > 0.5) {
      throw Error("Size must be between 0-0.5 MB for WS request");
    }
    const response = await this.statusClient.getRandomData({ sizeMbString: String(sizeMB) });
    return mapApiStatusData(response);
  }

  // --- PI-399: per-tool ALM RPC, routed by the session's alm_connection_type ---

  /** The legacy `almToolCall` picked the WAMP prefix from `almType`; the stubs are one client per tool. */
  private get almToolClient():
    | AlmsyncJiraApiClient
    | AlmsyncAdoApiClient
    | AlmsyncRallyApiClient
    | undefined {
    switch (this.almType) {
      case "jira":
        return this.almsyncJiraClient;
      case "ado":
        return this.almsyncAdoClient;
      case "ac":
        return this.almsyncRallyClient;
      default:
        return undefined;
    }
  }

  override async hasMappedAlmUser(id?: string): Promise<boolean> {
    const connectionId = this.getAlmConnectionId();
    if (!this.natsPiweb || !connectionId) return super.hasMappedAlmUser(id);
    const userId = id ?? this.userId;
    const response = await this.almUserMappingsClient.getUserMappings(
      { userIds: [userId] },
      connectionId,
    );
    return response.mappings.some(
      (mapping) => mapping.userId === userId && !mapping.unmappedReason,
    );
  }

  async isManualAlmUserMappingAllowed(): Promise<boolean> {
    const connectionId = this.getAlmConnectionId();
    if (!connectionId) return false;
    const response = await this.almUserMappingsClient.getSettings(connectionId);
    return response.settings.manual;
  }

  override async getAlmItemType(
    almType: string,
    boardType: BoardType,
    boardKeys: string[],
  ): Promise<ServerAlmItemTypeMap> {
    const client = this.almToolClient;
    if (!this.natsPiweb || !client || !this.almSyncId) {
      return super.getAlmItemType(almType, boardType, boardKeys);
    }
    const response = unwrapAlmItemType<NormalizedServerAlmItemTypeMap | ServerAlmItemTypeMap>(
      await client.getAlmItemType({ almTypeId: almType, boardType, boardKeys }, this.almSyncId),
      "alm_item_type.get",
    );
    return isNormalizedServerAlmItemTypeMap(response)
      ? denormalizedServerAlmItemTypeMap(response)
      : response;
  }

  override async getAlmItemTypeForSticky(stickyAlmId: string): Promise<ServerAlmItemType> {
    const client = this.almToolClient;
    if (!this.natsPiweb || !client || !this.almSyncId) {
      return super.getAlmItemTypeForSticky(stickyAlmId);
    }
    return unwrapAlmItemType<ServerAlmItemType>(
      await client.getAlmItemTypeForSticky({ stickyId: stickyAlmId }, this.almSyncId),
      "alm_item_type.for_sticky.get",
    );
  }

  override async getAlmStickyTypes(): Promise<ServerAlmStickyTypeData[]> {
    const client = this.almToolClient;
    if (!this.natsPiweb || !client || !this.almSyncId) return super.getAlmStickyTypes();
    return unwrapAlmTypes(await client.getAlmTypes(this.almSyncId));
  }

  override async getAlmSources(): Promise<ServerAlmSource[]> {
    const client = this.almToolClient;
    const connectionId = this.getAlmConnectionId();
    if (!this.natsPiweb || !client || !connectionId) return super.getAlmSources();
    return unwrapProjectSources(await client.getProjectSources(connectionId));
  }

  override async syncIteration(
    teamId: string,
    iterationId: number,
  ): Promise<ServerIterationSyncState> {
    const client = this.almToolClient;
    if (!this.natsPiweb || !client || !this.almSyncId) {
      return super.syncIteration(teamId, iterationId);
    }
    const response = await client.startIterationsSync(
      { teamId: Number(teamId), piIterationId: iterationId },
      this.almSyncId,
      iterationSyncTimeout,
    );
    return mapApiIterationSyncState(response);
  }

  override async syncBacklog(artId?: ArtId): Promise<ServerBacklogSyncState> {
    const client = this.almToolClient;
    if (!this.natsPiweb || !client || !this.almSyncId) return super.syncBacklog(artId);
    const response = await client.startSyncProgramBacklog(
      { ...(artId !== undefined && { artId }) },
      this.almSyncId,
      backlogSyncTimeout,
    );
    return mapApiBacklogSyncState(response);
  }

  override async getKanbanConfigs(
    teamIds: string[],
    force: boolean,
  ): Promise<ServerKanbanConfigMap> {
    const client = this.almToolClient;
    const connectionId = this.getAlmConnectionId();
    if (!this.natsPiweb || !client || !connectionId) {
      return super.getKanbanConfigs(teamIds, force);
    }
    const response = await client.getKanbanConfigs(
      { teamIds: teamIds.map(Number), force },
      connectionId,
    );
    return mapApiKanbanConfigs(response);
  }

  override async getObjectives(boardId: string, enriched: boolean): Promise<ServerObjectives> {
    if (!this.natsPiweb) return super.getObjectives(boardId, enriched);
    const response = await this.boardClient.getObjectives({ enriched }, boardId);
    return mapApiBoardObjectives(response);
  }

  // --- PI-397: board write requests ---

  override async newSticky(
    boardId: ServerSticky["board_id"],
    props: Partial<ServerSticky>,
  ): Promise<ServerSticky["id"]> {
    if (!this.natsPiweb) return super.newSticky(boardId, props);
    const response = await this.boardClient.createSticky(
      {
        properties: toApiPartialSticky(props),
        fromAlm: false,
        ...(this.almType && { almType: this.almType }),
      },
      boardId,
    );
    return response.id;
  }

  override async mirror(
    cardId: string,
    toBoardId: string,
    props?: Partial<ServerSticky>,
    aCenter?: ServerSticky["a_center"],
  ): Promise<ServerMirrorResponse> {
    if (!this.natsPiweb) return super.mirror(cardId, toBoardId, props, aCenter);
    const response = await this.boardClient.mirrorSticky(
      { properties: props ? toApiPartialSticky(props) : {}, ...(aCenter && { aCenter }) },
      toBoardId,
      cardId,
    );
    return { mirrored_id: response.mirroredId ?? null, created: response.created };
  }

  override move(cardId: string, teamId: string): void {
    if (!this.natsPiweb) return super.move(cardId, teamId);
    void this.stickyClient.moveToTeam({ teamId: Number(teamId), fromAlm: false }, cardId);
  }

  override async addReaction(cardId: string, reaction: Reaction): Promise<void> {
    if (!this.natsPiweb) return super.addReaction(cardId, reaction);
    await this.stickyClient.addReaction({ reaction }, cardId);
  }

  override async removeReaction(cardId: string, reaction: Reaction): Promise<void> {
    if (!this.natsPiweb) return super.removeReaction(cardId, reaction);
    await this.stickyClient.removeReaction({ reaction }, cardId);
  }

  stickyUpdateStarted(event: NoData, boardId: string, stickyId: string) {
    void this.boardPublisher.stickyUpdateStarted(event, this.sessionId, boardId, stickyId);
  }

  stickyUpdated(event: StickyUpdatedEvent, boardId: string, stickyId: string) {
    void this.boardPublisher.stickyUpdated(event, this.sessionId, boardId, stickyId);
  }

  stickyUpdateEnded(event: StickyUpdateEndedEvent, boardId: string, stickyId: string) {
    void this.boardPublisher.stickyUpdateEnded(event, this.sessionId, boardId, stickyId);
  }

  override async getSessions(): Promise<ServerSession[]> {
    if (!this.natsPiweb) return super.getSessions();
    const response = await this.sessionClient.getSessions();
    return response.sessions.map(mapApiSession);
  }

  // --- PI-425: board and session reads ---

  override async getBoardStickies(
    boardId: string,
    since?: number,
    withStatusDistribution = false,
  ): Promise<ServerSticky[] | ServerStickiesUpdate> {
    if (!this.natsPiweb) return super.getBoardStickies(boardId, since, withStatusDistribution);
    const response = await this.boardClient.getStickies(
      { ...(since !== undefined && { timestamp: since }), withStatusDistribution },
      boardId,
    );
    return mapApiStickiesResponse(response);
  }

  override async getStickies(
    stickyIds: string[],
    withLinks?: boolean,
    withStatusDistribution?: boolean,
  ): Promise<ServerSticky[]> {
    if (!this.natsPiweb) return super.getStickies(stickyIds, withLinks, withStatusDistribution);
    const response = await this.sessionClient.getStickies(
      {
        stickyIds,
        ...(withLinks !== undefined && { withLinks }),
        ...(withStatusDistribution !== undefined && { withStatusDistribution }),
      },
      this.sessionId,
    );
    return response.stickies.map((sticky) => mapApiSticky(sticky));
  }

  override async getBoardIterations(
    withStatusDistribution: boolean,
    boardId?: string,
  ): Promise<Record<string, ServerBoardIteration[]>> {
    if (!this.natsPiweb) return super.getBoardIterations(withStatusDistribution, boardId);
    const response = await this.sessionClient.getBoardIterations(
      { withStatusDistribution, ...(boardId !== undefined && { boardId }) },
      this.sessionId,
    );
    return mapApiBoardIterations(response);
  }

  override async searchStickies(query: SearchStickiesQuery): Promise<ServerSearchResult[]> {
    if (!this.natsPiweb) return super.searchStickies(query);
    const response = await this.sessionClient.searchStickies(
      {
        query: {
          ...(query.text !== undefined && { text: query.text }),
          ...(query.teams && { teamId: query.teams.map(({ id }) => (id ? Number(id) : null)) }),
          ...(query.types && { typeId: query.types.map(({ id }) => id) }),
          ...(query.statuses && { statuses: query.statuses.map(({ name }) => name) }),
          ...(query.flags && { flagType: query.flags.map(stickerToString) }),
          ...(query.boards && { boardId: query.boards.map(({ id }) => id) }),
          ...(query.assignees && {
            assigneeId: query.assignees.map((user) => (isNoUser(user) ? null : user.id)),
          }),
          ...(query.reporters && {
            reporterId: query.reporters.map((user) => (isNoUser(user) ? null : user.id)),
          }),
        },
      },
      this.sessionId,
    );
    return response.stickies.map(mapApiSearchResult);
  }

  override async getStickyTypes(): Promise<ServerStickyType[]> {
    if (!this.natsPiweb) return super.getStickyTypes();
    const response = await this.stickyTypeClient.getStickyTypes(this.sessionId);
    return response.stickyTypes
      .map(mapApiStickyType)
      .sort((a, b) => (a.index || 0) - (b.index || 0));
  }

  override async getBoardIdsOfGroupedStickies(
    stickyId: string,
  ): Promise<ServerStickyGroupBoardIds> {
    if (!this.natsPiweb) return super.getBoardIdsOfGroupedStickies(stickyId);
    const response = await this.sessionClient.getBoardIdsOfGroupedStickies(stickyId);
    return mapApiGroupBoards(response);
  }

  override async deleteObjectiveLink(
    cardId: string,
    boardId: string,
    objectiveId: string,
  ): Promise<void> {
    if (!this.natsPiweb) return super.deleteObjectiveLink(cardId, boardId, objectiveId);
    await this.sessionClient.deleteObjectiveLink({ stickyId: cardId, boardId, objectiveId });
  }
}

/**
 * Shared by diff.get and event_log.get, whose request models are separate
 * `$defs` that happen to match. The intersection fails here, rather than at a
 * call site, if the two specs ever diverge.
 */
function changeHistoryRequest(
  from: Date,
  to: Date,
  filters?: EventFilter[],
  changeTypes?: string[],
): GetDiffRequest & GetEventLogRequest {
  return {
    fromTime: from.getTime(),
    toTime: to.getTime(),
    ...(filters !== undefined && {
      filters: filters.map((filter) => ({
        property: filter.property,
        values: filter.values as Array<string | number>,
      })),
    }),
    ...(changeTypes !== undefined && { changeTypes }),
  };
}
